import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, Eye, Heart, Inbox, MessageSquare, TrendingUp, ExternalLink, Pencil } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const STAT_ICONS = {
  total_projects: FolderKanban,
  total_views: Eye,
  total_likes: Heart,
  new_inquiries: Inbox,
  pending_comments: MessageSquare,
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(console.error);
  }, []);

  const cards = stats ? [
    { key: "total_projects", label: "Total Projects", value: stats.total_projects, hint: `${stats.published_projects} published` },
    { key: "total_views", label: "Total Views", value: stats.total_views.toLocaleString(), hint: "All time" },
    { key: "total_likes", label: "Total Likes", value: stats.total_likes.toLocaleString(), hint: "Engagement" },
    { key: "new_inquiries", label: "New Inquiries", value: stats.new_inquiries, hint: "Awaiting response" },
    { key: "pending_comments", label: "Pending Comments", value: stats.pending_comments, hint: "Need approval" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-950">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Overview of your platform engagement and content.</p>
      </div>

      {/* Prominent live-editor CTA — the inline CMS lives on the public site */}
      <Link
        to="/"
        data-testid="open-live-editor"
        className="group relative block mb-6 sm:mb-8 overflow-hidden rounded-lg border border-slate-900 bg-slate-950 text-white p-5 sm:p-6 transition-shadow hover:shadow-lg"
      >
        <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-2">
              <Pencil className="w-3 h-3" />
              Live editor mode
            </div>
            <div className="font-heading text-lg sm:text-xl font-medium">Edit your gallery directly on the live site</div>
            <p className="text-slate-300 text-xs sm:text-sm mt-1 leading-relaxed">
              Tap the bio, profile photo, category cards, or any tile to edit in place. Empty slots become "Add Media" placeholders. The blue ADMIN bar at the top of the homepage is your control center.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 bg-white text-slate-950 px-4 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap group-hover:bg-emerald-400 transition-colors">
            Open live editor <ExternalLink className="w-3.5 h-3.5" />
          </span>
        </div>
      </Link>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8 sm:mb-10">
        {cards.map((c) => {
          const Icon = STAT_ICONS[c.key] || TrendingUp;
          return (
            <div key={c.key} className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 min-w-0" data-testid={`stat-${c.key}`}>
              <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 leading-tight truncate">{c.label}</div>
                <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </div>
              <div className="font-heading text-2xl sm:text-3xl font-medium text-slate-950 break-words">{c.value}</div>
              <div className="text-[11px] sm:text-xs text-slate-500 mt-1 truncate">{c.hint}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-medium text-slate-950 mb-3 sm:mb-4">Top Projects</h2>
          <div className="space-y-2 sm:space-y-3">
            {stats?.top_projects?.map((p) => (
              <Link
                to={`/admin/projects/edit/${p.id}`}
                key={p.id}
                className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-md transition-colors min-w-0"
                data-testid={`top-project-${p.slug}`}
              >
                <img src={fileUrl(p.cover_image)} alt={p.title} className="w-12 h-12 object-cover rounded-md flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-950 text-sm line-clamp-1">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex gap-3">
                    <span><Eye className="inline w-3 h-3 mr-1" />{p.view_count}</span>
                    <span><Heart className="inline w-3 h-3 mr-1" />{p.like_count}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-medium text-slate-950 mb-3 sm:mb-4">Quick Actions</h2>
          <div className="space-y-1.5 sm:space-y-2">
            <Link to="/admin/projects/new" className="block p-3 hover:bg-slate-50 rounded-md text-sm text-slate-700">+ Add new project</Link>
            <Link to="/admin/comments?status=pending" className="block p-3 hover:bg-slate-50 rounded-md text-sm text-slate-700">→ Review pending comments</Link>
            <Link to="/admin/inquiries" className="block p-3 hover:bg-slate-50 rounded-md text-sm text-slate-700">→ Review inquiries</Link>
            <Link to="/admin/settings" className="block p-3 hover:bg-slate-50 rounded-md text-sm text-slate-700">→ Update site settings</Link>
            <Link to="/admin/cleanup" className="block p-3 hover:bg-amber-50 rounded-md text-sm text-amber-800 border-l-2 border-amber-400" data-testid="link-database-cleanup">🧹 Database cleanup & sync audit</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
